import React, { useEffect, useState } from "react";

import { t } from "i18next";
import moment from "moment";

import { isProduct, ProductId } from "multitenancy/product";

import { DateInput } from "components";

import { LoadSpinner } from "features/LoadSpinner";

import { CheckBoxConfidential } from "components/CheckBoxConfidetial";
import { Drawer } from "components/Drawer";
import { DropdownGlobal } from "components/DropdownGlobal";
import TimeInput from "components/TimeInput";

import api from "services/api";
import { audienceRecordService } from "services/audienceRecord";
import { hearingFileServiceV2, IHearingFileBody } from "services/hearingFile";
import { HearingFileTypeService } from "services/hearingFileType";

import { LogOperation } from "utils/entities/logOperation";

import { CreateRecordConstants } from "./constants";
import { IContainerRecord } from "./interface";
import { Container, ContainerTwoComponents } from "./styles";
const FORMAT = "YYYY-MM-DD HH:mm";

export const CreateRecord: React.FC<IContainerRecord> = ({
  title,
  isEdit,
  setClose,
  isOpen,
  data,
  addToast,
  recordToEdit,
  setRecordEditedReturn,
  date,
  setDate,
  time,
  setTime,
  customerId,
}) => {
  const [dateError, setDateError] = useState<boolean>(false);
  const [timeError, setTimeError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const isInquerito = isProduct(ProductId.Inquerito);
  const [hearingFileTypes, setHearingFileTypes] = useState<any[]>([]);
  const [attendants, setAttendants] = useState<any[]>([]);
  const [hearingFileTypeId, setHearingFileTypeId] = useState<number | null>(
    null,
  );
  const [attendantId, setAttendantId] = useState<number | null>(null);
  const [confidential, setConfidential] = useState<boolean>(false);

  const cleanAll = () => {
    setDateError(false);
    setTimeError(false);
    setDate("");
    setTime("");
    setHearingFileTypeId(null);
    setAttendantId(null);
    setConfidential(false);
  };

  useEffect(() => {
    if (!isInquerito || !isOpen) return;
    if (customerId) {
      HearingFileTypeService.getAll(customerId, 1).then((res: any) => {
        if (res?.status && res?.data?.data) {
          setHearingFileTypes(
            res.data.data.filter((e: any) => e.active),
          );
        }
      });
    }
    if (data?.hearingId) {
      api
        .get(`/v1/Attendant/GetAllByHearingId?hearingId=${data.hearingId}`)
        .then((res: any) => {
          const list = res?.data?.data ?? res?.data ?? [];
          setAttendants(Array.isArray(list) ? list : []);
        })
        .catch(() => setAttendants([]));
    }
  }, [isInquerito, isOpen, customerId, data?.hearingId]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && recordToEdit) {
      setHearingFileTypeId(
        (recordToEdit as any)?.hearingFileTypeId ?? null,
      );
      setAttendantId((recordToEdit as any)?.attendantId ?? null);
      setConfidential(Boolean((recordToEdit as any)?.confidential));
    }
  }, [isOpen, isEdit, recordToEdit]);

  const handleClose = () => {
    cleanAll();
    setClose();
  };

  useEffect(() => {
    if (!isOpen) {
      cleanAll();
    }
  }, [isOpen]);

  const validateRequiredFields = () => {
    if (date && time) {
      return true;
    }

    setDateError(!date);
    setTimeError(!time);
    addToast({
      type: "warning",
      title: t(CreateRecordConstants.FILL_FIELDS_TITLE),
      description: t(CreateRecordConstants.FILL_FIELDS_DESCRIPTION),
    });
    return false;
  };

  const validateInputDate = () => {
    const inputDate = moment(`${date} ${time}`, FORMAT);
    const today = moment().startOf("minute");

    if (!inputDate.isBefore(today)) {
      return true;
    }

    setDateError(true);
    setTimeError(true);
    addToast({
      type: "warning",
      title: t(CreateRecordConstants.INVALID_DATE_TITLE),
      description: t(CreateRecordConstants.INVALID_DATE_DESCRIPTION),
    });
    return false;
  };

  const buildExpectedDate = () => new Date(`${date}T${time}Z`);

  const handleCreateRecord = async (expectedDate: Date) => {
    const response = await audienceRecordService.createAudienceRecord({
      expectedDate,
      hearingId: data?.hearingId || 0,
      userId: data?.userId || 0,
      hearingFileTypeId: hearingFileTypeId ?? undefined,
      attendantId: attendantId ?? undefined,
      confidential,
    });
    await LogOperation.postLogOperation("HearingFile", 1, 1);

    if (!response.status) {
      return;
    }

    handleClose();
    addToast({
      type: "success",
      title: t(CreateRecordConstants.CREATED_TITLE),
      description: t(CreateRecordConstants.CREATED_DESCRIPTION),
    });
  };

  const handleEditRecord = async (expectedDate: Date) => {
    if (!recordToEdit) {
      addToast({
        type: "error",
        title: t(CreateRecordConstants.NOT_FOUND_TITLE),
        description: t(CreateRecordConstants.NOT_FOUND_DESCRIPTION),
      });
      return;
    }

    const updateBody: IHearingFileBody = {
      ...recordToEdit,
      updateIn: new Date(),
      expectedDate,
    };
    const response = await hearingFileServiceV2.update(updateBody);

    if (response.status !== 200) {
      addToast({
        type: "error",
        title: t(CreateRecordConstants.EDITED_TITLE),
        description: t(CreateRecordConstants.EDIT_ERROR_DESCRIPTION),
      });
      return;
    }

    setRecordEditedReturn?.(updateBody);
    handleClose();
    addToast({
      type: "success",
      title: t(CreateRecordConstants.EDITED_TITLE),
      description: t(CreateRecordConstants.EDITED_DESCRIPTION),
    });
  };

  const handleApplyButton = async () => {
    if (!validateRequiredFields() || !validateInputDate()) {
      return;
    }

    setDateError(false);
    setTimeError(false);
    setLoading(true);

    try {
      const expectedDate = buildExpectedDate();

      if (!isEdit) {
        await handleCreateRecord(expectedDate);
        return;
      }

      await handleEditRecord(expectedDate);
    } finally {
      setLoading(false);
    }
  };

  const checkDateAndTime = (date?: string, time?: string) => {
    if (!date || !time) {
      setDateError(!date);
      setTimeError(!time);
      return;
    }

    const input = moment(`${date} ${time}`, FORMAT, true); // parse estrito
    if (!input.isValid()) {
      setDateError(true);
      setTimeError(true);
      return;
    }

    const now = moment();

    if (input.isSame(now, "day")) {
      setDateError(false);
      setTimeError(input.isBefore(now, "minute"));
      return;
    }

    if (input.isBefore(now, "day")) {
      setDateError(true);
      setTimeError(true);
      return;
    }

    setDateError(false);
    setTimeError(false);
  };

  const onChangeDate = (value?: string) => {
    setDate(value);
    checkDateAndTime(value, time);
  };

  const onChangeTime = (value?: string) => {
    setTime(value);
    if (date) {
      checkDateAndTime(date, value);
    }
  };

  return (
    <>
      <Drawer
        id="drawer-create-record"
        open={isOpen}
        onClose={handleClose}
        title={t(isEdit ? "common.editTitle" : "common.createTitle", { title })}
        onApply={handleApplyButton}
        applyLabel=""
        paddingLeft={33}
        paddingRight={33}
      >
        <Container>
          <ContainerTwoComponents>
            <DateInput
              label={t(CreateRecordConstants.OPEN_DATE)}
              setValue={(e: any) => onChangeDate(e)}
              value={date}
              required={true}
              error={dateError}
            />
            <div style={{ paddingTop: "2px", width: "200px" }}>
              <TimeInput
                label={t(CreateRecordConstants.TIME)}
                setValue={(e: any) => onChangeTime(e)}
                error={timeError}
                required={false}
                value={time}
              />
            </div>
          </ContainerTwoComponents>

          {isInquerito && (
            <>
              <DropdownGlobal
                width="100%"
                labelName={t("recordings.interviewType")}
                options={hearingFileTypes}
                editDropdown={
                  hearingFileTypes.find((h: any) => h.id === hearingFileTypeId) ??
                  null
                }
                setSelected={(o: any) => setHearingFileTypeId(o?.id ?? null)}
                wantAnObject={true}
              />

              <DropdownGlobal
                width="100%"
                labelName={t("recordings.participant")}
                options={attendants}
                editDropdown={
                  attendants.find((a: any) => a.id === attendantId) ?? null
                }
                setSelected={(o: any) => setAttendantId(o?.id ?? null)}
                disabled={!hearingFileTypeId}
                wantAnObject={true}
              />

              <CheckBoxConfidential
                label={t("audiences.confidential")}
                value={confidential}
                onChange={setConfidential}
              />
            </>
          )}
        </Container>
      </Drawer>
      <LoadSpinner loading={loading} />
    </>
  );
};

export default CreateRecord;
